<template>
  <div class="st-round-bar">
    <template v-if="hasOpenRound">
      <el-tag type="success" effect="plain" class="st-round-bar__badge">
        盘点中 {{ openRounds?.length || 1 }}
      </el-tag>
      <div class="st-round-bar__list">
        <div v-for="r in openRoundsDisplay" :key="r.round_id" class="st-round-bar__item">
          <div class="st-round-bar__item-head">
            <span class="st-round-bar__name">{{ r.name || `轮次 #${r.round_id}` }}</span>
            <el-tag size="small" type="info" effect="plain" class="st-round-bar__scope">
              {{ scopeText(r) }}
            </el-tag>
          </div>
          <el-button
            v-if="canEdit"
            type="warning"
            size="small"
            plain
            class="st-round-bar__end"
            :loading="actionLoading"
            @click="$emit('end', r.round_id)"
          >
            结束此轮
          </el-button>
        </div>
      </div>
    </template>
    <template v-else>
      <el-tag type="info" effect="plain" class="st-round-bar__badge">未开盘</el-tag>
      <span class="st-round-bar__hint">
        管理员可按整仓或指定区域开启盘点；盖章与「已盘」仅计入覆盖该库位的轮次
      </span>
    </template>
    <div class="st-round-bar__actions">
      <el-button
        v-if="canEdit"
        type="primary"
        size="small"
        :loading="actionLoading"
        @click="$emit('start')"
      >
        开启盘点
      </el-button>
      <el-button
        v-if="canAdminVoidStamps"
        type="danger"
        size="small"
        plain
        :loading="actionLoading"
        @click="$emit('void-before')"
      >
        批量作废印记
      </el-button>
      <el-button size="small" @click="$emit('history')">盘点历史</el-button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  openRound: { type: Object, default: null },
  openRounds: { type: Array, default: null },
  hasOpenRound: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
  /** 仅系统管理员：批量作废印记 */
  canAdminVoidStamps: { type: Boolean, default: false },
  actionLoading: { type: Boolean, default: false },
  progressText: { type: String, default: '' },
  scopeSummaryFn: { type: Function, default: null }
})
defineEmits(['start', 'end', 'history', 'void-before'])

const openRoundsDisplay = computed(() => {
  if (Array.isArray(props.openRounds) && props.openRounds.length) return props.openRounds
  return props.openRound ? [props.openRound] : []
})

function scopeText(r) {
  if (typeof props.scopeSummaryFn === 'function') return props.scopeSummaryFn(r)
  if ((r?.scope_mode || 'warehouse') === 'partial') {
    const n = (r.scope_location_ids || []).length
    return n ? `区域×${n}` : '指定范围'
  }
  return '整仓'
}
</script>

<style scoped>
.st-round-bar {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #f5f7fa;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  max-width: 100%;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.st-round-bar__badge {
  align-self: flex-start;
  max-width: 100%;
}

.st-round-bar__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  width: 100%;
}

.st-round-bar__item {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  padding: 8px 10px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  box-sizing: border-box;
}

.st-round-bar__item-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
}

.st-round-bar__name {
  font-weight: 600;
  color: #303133;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
}

.st-round-bar__scope {
  max-width: 100%;
  height: auto !important;
  white-space: normal !important;
  line-height: 1.35 !important;
}

.st-round-bar__scope :deep(.el-tag__content) {
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.st-round-bar__end {
  width: 100%;
  margin: 0 !important;
}

.st-round-bar__hint {
  color: #606266;
  font-size: 13px;
  line-height: 1.45;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.st-round-bar__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.st-round-bar__actions :deep(.el-button) {
  width: 100%;
  margin: 0 !important;
}

@media (min-width: 768px) {
  .st-round-bar {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .st-round-bar__list {
    flex: 1 1 240px;
  }
  .st-round-bar__item {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  .st-round-bar__item-head {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
  }
  .st-round-bar__end {
    width: auto;
    flex-shrink: 0;
  }
  .st-round-bar__actions {
    flex-direction: row;
    width: auto;
    margin-left: auto;
  }
  .st-round-bar__actions :deep(.el-button) {
    width: auto;
  }
}
</style>
